import { CSSVarsTokenMapper } from '@genesislcap/foundation-ui';
import { css } from '@microsoft/fast-element';
import { DI } from '@microsoft/fast-foundation';
/**
 * All official AG CSS variables
 * @public
 */
export var AgGridCSSVars;
(function (AgGridCSSVars) {
    AgGridCSSVars["foregroundColor"] = "--ag-foreground-color";
    AgGridCSSVars["dataColor"] = "--ag-data-color";
    AgGridCSSVars["secondaryForegroundColor"] = "--ag-secondary-foreground-color";
    AgGridCSSVars["headerForegroundColor"] = "--ag-header-foreground-color";
    AgGridCSSVars["disabledForegroundColor"] = "--ag-disabled-foreground-color";
    AgGridCSSVars["backgroundColor"] = "--ag-background-color";
    AgGridCSSVars["headerBackgroundColor"] = "--ag-header-background-color";
    AgGridCSSVars["subheaderBackgroundColor"] = "--ag-subheader-background-color";
    AgGridCSSVars["subheaderToolbarBackgroundColor"] = "--ag-subheader-toolbar-background-color";
    AgGridCSSVars["controlPanelBackgroundColor"] = "--ag-control-panel-background-color";
    AgGridCSSVars["selectedRowBackgroundColor"] = "--ag-selected-row-background-color";
    AgGridCSSVars["oddRowBackgroundColor"] = "--ag-odd-row-background-color";
    AgGridCSSVars["rowHoverColor"] = "--ag-row-hover-color";
    AgGridCSSVars["rangeSelectionBorderColor"] = "--ag-range-selection-border-color";
    AgGridCSSVars["rangeSelectionBackgroundColor"] = "--ag-range-selection-background-color";
    AgGridCSSVars["selectedTabUnderlineColor"] = "--ag-selected-tab-underline-color";
    AgGridCSSVars["rangeSelectionChartCategoryBackgroundColor"] = "--ag-range-selection-chart-category-background-color";
    AgGridCSSVars["rangeSelectionChartBackgroundColor"] = "--ag-range-selection-chart-background-color";
    AgGridCSSVars["headerCellHoverBackgroundColor"] = "--ag-header-cell-hover-background-color";
    AgGridCSSVars["headerCellMovingBackgroundColor"] = "--ag-header-cell-moving-background-color";
    AgGridCSSVars["chipBackgroundColor"] = "--ag-chip-background-color";
    AgGridCSSVars["borderColor"] = "--ag-border-color";
    AgGridCSSVars["secondaryBorderColor"] = "--ag-secondary-border-color";
    AgGridCSSVars["rowBorderColor"] = "--ag-row-border-color";
    AgGridCSSVars["headerColumnSeparatorColor"] = "--ag-header-column-separator-color";
    AgGridCSSVars["headerColumnResizeHandleColor"] = "--ag-header-column-resize-handle-color";
    AgGridCSSVars["inputBorderColor"] = "--ag-input-border-color";
    AgGridCSSVars["inputDisabledBorderColor"] = "--ag-input-disabled-border-color";
    AgGridCSSVars["inputDisabledBackgroundColor"] = "--ag-input-disabled-background-color";
    AgGridCSSVars["checkboxBackgroundColor"] = "--ag-checkbox-background-color";
    AgGridCSSVars["checkboxCheckedColor"] = "--ag-checkbox-checked-color";
    AgGridCSSVars["checkboxUncheckedColor"] = "--ag-checkbox-unchecked-color";
    AgGridCSSVars["checkboxIndeterminateColor"] = "--ag-checkbox-indeterminate-color";
    AgGridCSSVars["toggleButtonOffBorderColor"] = "--ag-toggle-button-off-border-color";
    AgGridCSSVars["toggleButtonOffBackgroundColor"] = "--ag-toggle-button-off-background-color";
    AgGridCSSVars["toggleButtonOnBorderColor"] = "--ag-toggle-button-on-border-color";
    AgGridCSSVars["toggleButtonOnBackgroundColor"] = "--ag-toggle-button-on-background-color";
    AgGridCSSVars["toggleButtonSwitchBackgroundColor"] = "--ag-toggle-button-switch-background-color";
    AgGridCSSVars["toggleButtonSwitchBorderColor"] = "--ag-toggle-button-switch-border-color";
    AgGridCSSVars["minichartSelectedChartColor"] = "--ag-minichart-selected-chart-color";
    AgGridCSSVars["minichartSelectedPageColor"] = "--ag-minichart-selected-page-color";
    AgGridCSSVars["modalOverlayBackgroundColor"] = "--ag-modal-overlay-background-color";
    AgGridCSSVars["inputFocusBoxShadow"] = "--ag-input-focus-box-shadow";
    AgGridCSSVars["inputFocusBorderColor"] = "--ag-input-focus-border-color";
})(AgGridCSSVars || (AgGridCSSVars = {}));
/**
 * The available Grid Pro themes.
 * @public
 */
export var AgGridTheme;
(function (AgGridTheme) {
    AgGridTheme["alpine"] = "ag-theme-alpine";
    AgGridTheme["alpineDark"] = "ag-theme-alpine-dark";
    AgGridTheme["balham"] = "ag-theme-balham";
    AgGridTheme["balhamDark"] = "ag-theme-balham-dark";
    AgGridTheme["material"] = "ag-theme-material";
})(AgGridTheme || (AgGridTheme = {}));
/**
 * Generates the class name for a given Grid Pro theme name.
 * @param themeName - the Grid Pro theme name
 * @returns the class name for the Grid Pro token map
 * @public
 * @example The result of this will be a class name that is applied to the grid part of GridPro template (actual content of the class comes from agThemeTokenMapCSS):
 * ```html
 * <span part="grid" class="ag-theme-genesis-rapid-dark">
 * ```
 */
export const agThemeTokenMapClassname = (themeName) => `${themeName}-token-map`;
/**
 * Generates the id name for custom Grid Pro fonts.
 * @param themeName - the Grid Pro theme name
 * @returns the id name for the Grid Pro fonts
 * @public
 * @example The result of this is used in the id of style tag:
 * ```html
 * <style id="ag-theme-alpine-fonts"></style>
 * ```
 */
export const agThemeFontsId = (themeName) => `${themeName}-fonts`;
/**
 * Generates the CSS for a given GridPro theme name and token map
 * @param themeName - the GridPro theme name
 * @param tokenMap - a map of CSS variables {@link @genesislcap/grid-pro#AgGridCSSVars} and custom token values
 * @returns the CSS for the Grid Pro token map
 * @public
 * @example
 * ```ts
 * export const agThemeGenesisRapidTokens = {
 *  backgroundColor: create<Swatch>('rapid-ag-background-color').withDefault((elem: HTMLElement) =>
 *    baseLayerLuminance.getValueFor(elem) === StandardLuminance.LightMode
 *      ? lightColors.backgroundSwatch
 *      : darkColors.backgroundSwatch
 *  ),
 *  foregroundColor: create<Swatch>('rapid-ag-foreground-color').withDefault((elem: HTMLElement) =>
 *    baseLayerLuminance.getValueFor(elem) === StandardLuminance.LightMode
 *      ? lightColors.foregroundSwatch
 *      : darkColors.foregroundSwatch
 *  ),
 * };
 * export const agThemeGenesisRapidTokenMap: CSSVarTokenMap<AgGridCSSVars | string> = {
 *  [AgGridCSSVars.backgroundColor]: agThemeGenesisRapidTokens.backgroundColor,
 *  [AgGridCSSVars.foregroundColor]: agThemeGenesisRapidTokens.foregroundColor,
 * };
 * ```
 */
export const agThemeTokenMapCSS = (themeName, tokenMap) => css `
  .${agThemeTokenMapClassname(themeName)} {
    ${new CSSVarsTokenMapper(tokenMap)}
  }
`;
/**
 * The available Grid Pro Renderer Types
 * @public
 */
export var GridProRendererTypes;
(function (GridProRendererTypes) {
    GridProRendererTypes["action"] = "action";
    GridProRendererTypes["actionsMenu"] = "actionsMenu";
    GridProRendererTypes["boolean"] = "boolean";
    GridProRendererTypes["editable"] = "editable";
    GridProRendererTypes["text"] = "text";
    GridProRendererTypes["select"] = "select";
    GridProRendererTypes["dateEditor"] = "dateEditor";
    GridProRendererTypes["selectEditor"] = "selectEditor";
    GridProRendererTypes["numberEditor"] = "numberEditor";
    GridProRendererTypes["stringEditor"] = "stringEditor";
    GridProRendererTypes["multiselectEditor"] = "multiselectEditor";
})(GridProRendererTypes || (GridProRendererTypes = {}));
/**
 * Event fired when grid is ready
 * @public
 */
export const GRID_READY_EVENT = 'onGridReady';
/**
 * Key used to store currently edited column when it being synced with server side data
 * @public
 */
export const EDITED_COLUMN_FIELD = '__editedColumn';
/**
 * Key used to store error details (column, message) when inline edit fails sync with server side data
 * @public
 */
export const ERROR_COLUMN_FIELD = '__errorColumn';
/**
 * Default grid options
 * @public
 */
export const defaultGridOptionsConfig = {};
/**
 * Grid options config used to inject into grids in application
 * @internal
 */
export const GridOptionsConfig = DI.createInterface((x) => x.instance(defaultGridOptionsConfig));
